import React, { useState, useEffect, useMemo } from 'react';
import { APP_DISPLAY_NAME } from '../config/brand';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useLocation, useNavigate } from 'react-router-dom';
import orderService from '../services/orderService';
import OrderHistory from '../components/OrderHistory';
import OrderStatusTracker from '../components/OrderStatusTracker';
import { Order } from '../types/cart';
import { getOrderDisplayTitle } from '../utils/orderDisplay';
import { Notification } from '../types/notification';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  panelPadding?: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, panelPadding = 3, ...other } = props;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: panelPadding }}>{children}</Box>}
    </div>
  );
}

type DashboardListFilter = 'overview' | 'active' | 'pending' | 'completed';

/** Chaavi tokens (chaavi_orders_dashboard.html) */
const C = {
  page: '#f5f2ec',
  ink: '#3a2e1e',
  muted: '#a89880',
  border: '#e8e0d4',
  rule: '#f5f0e8',
  accent: '#c0873a',
  pendingBar: '#e8a020',
  completedBar: '#4a9e6a',
  warmBrown: '#5a4a35',
  chipActiveBg: '#fdf0e0',
  chipActiveText: '#b07030',
  chipPendingBg: '#fef4e0',
  chipPendingText: '#a07020',
  chipDoneBg: '#e8f4ed',
  chipDoneText: '#3a7a50',
  chipCancelBg: '#fdecea',
  chipCancelText: '#a03020',
};

const ORDER_THUMBS = ['🎊', '📸', '💐', '🎵', '💄', '🚗', '🍽️', '✨'];

function orderThumbEmoji(orderId: number): string {
  return ORDER_THUMBS[Math.abs(orderId) % ORDER_THUMBS.length];
}

function statusCategory(status: Order['status']): 'active' | 'pending' | 'completed' | 'cancelled' {
  if (status === 'DELIVERED') return 'completed';
  if (status === 'CANCELLED') return 'cancelled';
  if (status === 'PENDING') return 'pending';
  return 'active';
}

function statusPillSx(cat: ReturnType<typeof statusCategory>) {
  const map = {
    active: { bg: C.chipActiveBg, color: C.chipActiveText },
    pending: { bg: C.chipPendingBg, color: C.chipPendingText },
    completed: { bg: C.chipDoneBg, color: C.chipDoneText },
    cancelled: { bg: C.chipCancelBg, color: C.chipCancelText },
  }[cat];
  return {
    fontSize: '0.6875rem',
    py: 0.375,
    px: 1.25,
    borderRadius: '10px',
    fontFamily: '"Jost", "Roboto", sans-serif',
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    bgcolor: map.bg,
    color: map.color,
  };
}

const IconStatClock: React.FC = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18, strokeWidth: 1.6, fill: 'none' }}>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </Box>
);

const IconStatAlert: React.FC = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18, strokeWidth: 1.6, fill: 'none' }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </Box>
);

const IconStatCheck: React.FC = () => (
  <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18, strokeWidth: 1.6, fill: 'none' }}>
    <polyline points="20 6 9 17 4 12" />
  </Box>
);

const IconRefresh: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 14, height: 14, strokeWidth: 1.8, fill: 'none', stroke: 'currentColor' }}
  >
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 .49-5" />
  </Box>
);

const ClientDashboard: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { notifications, unreadCount: unreadNotificationCount, markAsRead, markAllAsRead, refreshNotifications } = useNotifications();
  const [activeTab, setActiveTab] = useState(0);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [dashboardFilter, setDashboardFilter] = useState<DashboardListFilter>('overview');

  const ordersArray = useMemo(() => (Array.isArray(orders) ? orders : []), [orders]);

  const dashboardStats = useMemo(() => {
    const active = ordersArray.filter(o => ['CONFIRMED', 'PREPARING', 'READY'].includes(o.status)).length;
    const pending = ordersArray.filter(o => o.status === 'PENDING').length;
    const completed = ordersArray.filter(o => o.status === 'DELIVERED').length;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const activeThisWeek = ordersArray.filter(
      o =>
        ['CONFIRMED', 'PREPARING', 'READY'].includes(o.status) &&
        new Date(o.orderDate).getTime() >= weekAgo
    ).length;
    return { active, pending, completed, activeThisWeek };
  }, [ordersArray]);

  const dashboardListOrders = useMemo(() => {
    let list: Order[];
    switch (dashboardFilter) {
      case 'active':
        list = ordersArray.filter(o => ['CONFIRMED', 'PREPARING', 'READY'].includes(o.status));
        break;
      case 'pending':
        list = ordersArray.filter(o => o.status === 'PENDING');
        break;
      case 'completed':
        list = ordersArray.filter(o => o.status === 'DELIVERED');
        break;
      default:
        list = [...ordersArray];
    }
    return list.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
  }, [ordersArray, dashboardFilter]);

  const fetchOrders = React.useCallback(async () => {
    if (!user?.phoneNumber) return;
    
    try {
      setLoading(true);
      setError(null);
      const ordersData = await orderService.getOrdersByUserId(user.phoneNumber);
      
      // Ensure ordersData is always an array
      const ordersArray = Array.isArray(ordersData) ? ordersData : [];
      
      console.log(`Setting ${ordersArray.length} orders in state`);
      
      // Always set orders, even if empty array
      setOrders(ordersArray);
      
      if (ordersArray.length === 0) {
        console.log('No orders found for user:', user.phoneNumber);
      }
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to fetch orders');
      // Set empty array on error to prevent stale data
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user?.phoneNumber]);

  // Handle navigation state to set active tab
  useEffect(() => {
    const state = location.state as { activeTab?: number; orderId?: number; notificationId?: number } | null;
    if (state?.activeTab !== undefined) {
      setActiveTab(state.activeTab);
      
      // If navigating to My Orders tab (index 1) from a notification, refresh orders
      // but don't auto-open the dialog - let user see all orders first
      if (state.activeTab === 1) {
        fetchOrders();
      }
      
      // Don't auto-open order detail dialog - let user see all orders first
      // They can click on a specific order from the list if they want details
      
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, fetchOrders]);

  // NotificationContext handles all notification fetching and syncing
  // No need for separate useEffect hooks here

  useEffect(() => {
    if (location.state?.orderId) {
      // Scroll to the order or highlight it
      // This will be handled by the order list component
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, fetchOrders]);

  useEffect(() => {
    if (user?.phoneNumber) {
      fetchOrders();
    }
  }, [user, fetchOrders]);

  const handleNotificationClick = async (notification: Notification) => {
    // Always mark as read when notification is clicked/opened
    // The backend will handle if it's already read
    try {
      await markAsRead(notification.notificationId);
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      // Continue to order details even if marking as read fails
    }
    
    // If notification has an orderId, switch to My Orders tab to show all orders
    // User can then click on the specific order if they want details
    if (notification.orderId) {
      setActiveTab(1); // Switch to My Orders tab (index 1)
      // Refresh orders to ensure we have the latest data
      fetchOrders();
      // Don't open the dialog automatically - let user see all orders first
      // They can click on the specific order from the list if needed
    } else {
      // If no orderId, switch to notifications tab
      setActiveTab(2);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    
    // Close order detail dialog when switching tabs to ensure all orders are visible
    if (orderDetailOpen) {
      setOrderDetailOpen(false);
      setSelectedOrder(null);
    }
    
    if (newValue === 1) { // Orders tab
      fetchOrders();
    }
    // Notifications tab doesn't need manual refresh - NotificationContext handles it
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setOrderDetailOpen(true);
  };

  const handleCloseOrderDetail = () => {
    setOrderDetailOpen(false);
    setSelectedOrder(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'CONFIRMED': return 'info';
      case 'PREPARING': return 'primary';
      case 'READY': return 'success';
      case 'DELIVERED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  const getOrderSteps = (order: Order) => {
    const steps = [
      { 
        label: 'Order Placed', 
        completed: true,
        description: `Order placed on ${new Date(order.orderDate).toLocaleString()}`,
        icon: <ShoppingCartIcon />
      },
      { 
        label: 'Order Confirmed', 
        completed: order.status !== 'PENDING',
        description: order.status !== 'PENDING' ? 'Order confirmed by vendor' : 'Waiting for confirmation',
        icon: <CheckCircleIcon />
      },
      { 
        label: 'Preparing', 
        completed: ['PREPARING', 'READY', 'DELIVERED'].includes(order.status),
        description: ['PREPARING', 'READY', 'DELIVERED'].includes(order.status) ? 'Your order is being prepared' : 'Waiting to start preparation',
        icon: <RefreshIcon />
      },
      { 
        label: 'Ready for Delivery', 
        completed: ['READY', 'DELIVERED'].includes(order.status),
        description: ['READY', 'DELIVERED'].includes(order.status) ? 'Order is ready for delivery' : 'Order is being prepared',
        icon: <ShippingIcon />
      },
      { 
        label: 'Delivered', 
        completed: order.status === 'DELIVERED',
        description: order.status === 'DELIVERED' ? 'Order has been delivered' : 'Order is ready for delivery',
        icon: <CheckCircleIcon />
      },
    ];
    
    if (order.status === 'CANCELLED') {
      return steps.map(step => ({ ...step, completed: false, description: 'Order was cancelled' }));
    }
    
    return steps;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        sx={{ bgcolor: C.page }}
      >
        <CircularProgress sx={{ color: C.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2, bgcolor: C.page, p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const mainTabButton = (index: number, label: React.ReactNode) => (
    <Box
      component="button"
      type="button"
      onClick={e => handleTabChange(e, index)}
      sx={{
        py: 1.25,
        px: { xs: 1.5, sm: 2.25 },
        border: 'none',
        background: 'none',
        fontSize: '0.8125rem',
        color: activeTab === index ? C.accent : C.muted,
        cursor: 'pointer',
        fontFamily: '"Jost", "Roboto", sans-serif',
        borderBottom: '2px solid',
        borderColor: activeTab === index ? C.accent : 'transparent',
        mb: '-0.5px',
        fontWeight: activeTab === index ? 500 : 400,
        whiteSpace: 'nowrap',
        '&:hover': { color: C.warmBrown },
      }}
    >
      {label}
    </Box>
  );

  const filterTabButton = (filter: DashboardListFilter, label: string) => (
    <Box
      component="button"
      type="button"
      onClick={() => setDashboardFilter(filter)}
      sx={{
        py: 1.25,
        px: { xs: 1.25, sm: 2.25 },
        border: 'none',
        background: 'none',
        fontSize: '0.8125rem',
        color: dashboardFilter === filter ? C.accent : C.muted,
        cursor: 'pointer',
        fontFamily: '"Jost", "Roboto", sans-serif',
        borderBottom: '2px solid',
        borderColor: dashboardFilter === filter ? C.accent : 'transparent',
        mb: '-0.5px',
        fontWeight: dashboardFilter === filter ? 500 : 400,
        '&:hover': { color: C.warmBrown },
      }}
    >
      {label}
    </Box>
  );

  const statCard = (
    filter: DashboardListFilter,
    topColor: string,
    variant: 'active' | 'pending' | 'completed',
    count: number,
    label: string,
    trend: string | null,
    trendOk: boolean,
    IconCmp: React.FC
  ) => {
    const iconBg =
      variant === 'active'
        ? C.chipActiveBg
        : variant === 'pending'
        ? C.chipPendingBg
        : C.chipDoneBg;
    const iconColor =
      variant === 'active' ? C.accent : variant === 'pending' ? C.pendingBar : C.completedBar;
    const selected = dashboardFilter === filter;
    return (
      <Box
        component="button"
        type="button"
        onClick={() => setDashboardFilter(filter)}
        sx={{
          bgcolor: '#fff',
          borderRadius: '12px',
          border: '0.5px solid',
          borderColor: selected ? C.accent : C.border,
          p: 2.5,
          pt: 2,
          pb: 2,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          textAlign: 'left',
          width: '100%',
          transition: 'border-color 0.14s',
          font: 'inherit',
          color: 'inherit',
          '&:hover': { borderColor: C.accent },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            borderRadius: '12px 12px 0 0',
            bgcolor: topColor,
          },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '9px',
            bgcolor: iconBg,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
            mt: 0.5,
            '& svg': { stroke: 'currentColor' },
          }}
        >
          <IconCmp />
        </Box>
        <Typography
          sx={{
            fontSize: '1.75rem',
            fontWeight: 500,
            color: C.ink,
            fontFamily: '"Jost", "Roboto", sans-serif',
            lineHeight: 1,
          }}
        >
          {count}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: C.muted,
            fontFamily: '"Jost", "Roboto", sans-serif',
            mt: 0.5,
          }}
        >
          {label}
        </Typography>
        {trend ? (
          <Typography
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              fontSize: '0.6875rem',
              fontFamily: '"Jost", "Roboto", sans-serif',
              px: 1,
              py: 0.375,
              borderRadius: '8px',
              bgcolor: trendOk ? C.chipDoneBg : C.chipActiveBg,
              color: trendOk ? C.chipDoneText : C.chipActiveText,
              fontWeight: 500,
            }}
          >
            {trend}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const formatOrderMeta = (order: Order): string => {
    const first = order.orderItems?.[0];
    const biz = first?.businessName ?? 'Vendor';
    const ordered = new Date(order.orderDate).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    const cat = statusCategory(order.status);
    if (cat === 'completed') {
      return `Delivered · ${biz}`;
    }
    if (cat === 'pending') {
      return `Awaiting vendor · ${biz}`;
    }
    if (order.deliveryDate) {
      try {
        const d = new Date(order.deliveryDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        return `Target ${d} · ${biz}`;
      } catch {
        /* fall through */
      }
    }
    return `Ordered ${ordered} · ${biz}`;
  };

  return (
    <Box sx={{ bgcolor: C.page, minHeight: '100%', width: '100%' }}>
      <Box sx={{ borderBottom: '0.5px solid', borderColor: C.border, mb: 2.5, display: 'flex', flexWrap: 'wrap', gap: 0 }}>
        {mainTabButton(0, 'Dashboard')}
        {mainTabButton(
          1,
          ordersArray.length > 0 ? `My orders (${ordersArray.length})` : 'My orders'
        )}
        {mainTabButton(
          2,
          unreadNotificationCount > 0 ? `Notifications (${unreadNotificationCount})` : 'Notifications'
        )}
      </Box>

      {/* Chaavi dashboard (chaavi_orders_dashboard.html) */}
      <TabPanel value={activeTab} index={0} panelPadding={0}>
        <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 1, pb: 5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.375rem' },
                  fontWeight: 500,
                  color: C.ink,
                  fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
                  lineHeight: 1.3,
                }}
              >
                My orders dashboard
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: C.muted,
                  fontFamily: '"Jost", "Roboto", sans-serif',
                  mt: 0.5,
                  maxWidth: 480,
                }}
              >
                Track your bookings and get real-time updates from vendors.
              </Typography>
            </Box>
            <Button
              variant="contained"
              disableElevation
              type="button"
              startIcon={<IconRefresh />}
              onClick={() => {
                fetchOrders();
                refreshNotifications();
              }}
              sx={{
                borderRadius: '8px',
                px: 2.75,
                py: 1.125,
                textTransform: 'none',
                fontFamily: '"Jost", "Roboto", sans-serif',
                fontSize: '0.8125rem',
                fontWeight: 500,
                bgcolor: C.accent,
                color: '#fff',
                flexShrink: 0,
                '&:hover': { bgcolor: '#a87030' },
                '& .MuiButton-startIcon': { color: '#fff' },
              }}
            >
              Refresh
            </Button>
          </Box>

          <Grid container spacing={1.75} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              {statCard(
                'active',
                C.accent,
                'active',
                dashboardStats.active,
                'Active orders',
                dashboardStats.activeThisWeek > 0 ? `${dashboardStats.activeThisWeek} this week` : null,
                false,
                IconStatClock
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {statCard(
                'pending',
                C.pendingBar,
                'pending',
                dashboardStats.pending,
                'Pending orders',
                dashboardStats.pending > 0 ? 'needs action' : null,
                false,
                IconStatAlert
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              {statCard(
                'completed',
                C.completedBar,
                'completed',
                dashboardStats.completed,
                'Completed orders',
                dashboardStats.completed > 0 && dashboardStats.pending === 0 ? 'all caught up' : null,
                true,
                IconStatCheck
              )}
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', borderBottom: '0.5px solid', borderColor: C.border, mb: 2.5 }}>
            {filterTabButton('overview', 'Overview')}
            {filterTabButton('active', 'Active')}
            {filterTabButton('pending', 'Pending')}
            {filterTabButton('completed', 'Completed')}
          </Box>

          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '12px',
              border: '0.5px solid',
              borderColor: C.border,
              overflow: 'hidden',
            }}
          >
            {dashboardListOrders.length === 0 ? (
              <Box sx={{ py: 7, px: 2.5, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: '#f5f0e8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 1.75,
                  }}
                >
                  <ShoppingCartIcon sx={{ fontSize: 26, color: '#c0b090' }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.9375rem',
                    color: C.warmBrown,
                    fontFamily: '"Jost", "Roboto", sans-serif',
                    fontWeight: 500,
                  }}
                >
                  No orders yet
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    color: C.muted,
                    fontFamily: '"Jost", "Roboto", sans-serif',
                    mt: 0.625,
                  }}
                >
                  Start exploring businesses and place your first order!
                </Typography>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => navigate('/explore')}
                  sx={{
                    mt: 2,
                    borderRadius: '8px',
                    px: 2.75,
                    py: 1.25,
                    textTransform: 'none',
                    fontFamily: '"Jost", "Roboto", sans-serif',
                    fontSize: '0.8125rem',
                    bgcolor: C.accent,
                    '&:hover': { bgcolor: '#a87030' },
                  }}
                >
                  Explore {APP_DISPLAY_NAME}
                </Button>
              </Box>
            ) : (
              dashboardListOrders.map((order, idx) => {
                const cat = statusCategory(order.status);
                const pillLabel =
                  cat === 'active'
                    ? 'Active'
                    : cat === 'pending'
                    ? 'Pending'
                    : cat === 'completed'
                    ? 'Completed'
                    : 'Cancelled';
                return (
                  <Box
                    key={order.orderId}
                    onClick={() => handleViewOrder(order)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.75,
                      py: 2,
                      px: 2.5,
                      borderBottom: idx < dashboardListOrders.length - 1 ? '0.5px solid' : 'none',
                      borderColor: C.rule,
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                      '&:hover': { bgcolor: '#fdfaf5' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '9px',
                        bgcolor: '#f0ebe0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: '1.25rem',
                      }}
                    >
                      {orderThumbEmoji(order.orderId)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: C.ink,
                          fontFamily: '"Jost", "Roboto", sans-serif',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {getOrderDisplayTitle(order)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: C.muted,
                          fontFamily: '"Jost", "Roboto", sans-serif',
                          mt: 0.25,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatOrderMeta(order)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                      <Typography component="span" sx={statusPillSx(cat)}>
                        {pillLabel}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: C.ink,
                          fontFamily: '"Jost", "Roboto", sans-serif',
                        }}
                      >
                        ₹{order.totalAmount.toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </TabPanel>

      {/* Orders Tab */}
      <TabPanel value={activeTab} index={1}>
        {ordersArray.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" component="h2">
                Live Order Status ({ordersArray.length} {ordersArray.length === 1 ? 'order' : 'orders'})
              </Typography>
              <Button 
                variant="outlined" 
                onClick={() => {
                  fetchOrders();
                  refreshNotifications();
                }}
                disabled={loading}
              >
                Refresh Status
              </Button>
            </Box>
            <Grid container spacing={3}>
              {ordersArray.map((order) => (
                <Grid item xs={12} key={order.orderId}>
                  <OrderStatusTracker 
                    order={order} 
                    showDetails={false}
                    onCancel={() => {
                      fetchOrders();
                      refreshNotifications();
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 4 }} />
          </Box>
        )}
        <OrderHistory 
          orders={ordersArray}
          loading={loading}
          onRefresh={() => {
            fetchOrders();
            refreshNotifications();
          }}
          onViewOrder={handleViewOrder}
        />
      </TabPanel>

      {/* Notifications Tab (Separate) */}
      <TabPanel value={activeTab} index={2}>
        {notifications.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <NotificationIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No notifications yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You'll receive updates about your orders here.
            </Typography>
          </Paper>
        ) : (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Typography variant="h5" component="h2" gutterBottom>
                  Notifications
                </Typography>
                {unreadNotificationCount > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {unreadNotificationCount} unread {unreadNotificationCount === 1 ? 'notification' : 'notifications'}
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={1}>
                {unreadNotificationCount > 0 && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={async () => {
                      try {
                        await markAllAsRead();
                      } catch (err: any) {
                        console.error('Error marking all notifications as read:', err);
                        setError(err.message || 'Failed to mark all notifications as read');
                      }
                    }}
                  >
                    Mark All as Read
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={refreshNotifications}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
            <List>
            {notifications.map((notification) => (
              <ListItem 
                key={notification.notificationId} 
                divider
                onClick={() => handleNotificationClick(notification)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  },
                  backgroundColor: !notification.isRead ? 'action.selected' : 'transparent'
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: notification.isRead ? 'grey.300' : 'primary.main' }}>
                    <NotificationIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: notification.isRead ? 'normal' : 'bold',
                        color: notification.isRead ? 'text.primary' : 'primary.main'
                      }}
                    >
                      {notification.message}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(notification.createdAt).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {notification.businessName}
                      </Typography>
                      {notification.notificationType === 'STOCK_AVAILABLE' && notification.deliveryDate && (
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                          Available Date: {new Date(notification.deliveryDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {!notification.isRead && (
                    <Chip label="New" color="primary" size="small" />
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          </Box>
        )}
      </TabPanel>

      {/* Order Detail Dialog */}
      <Dialog 
        open={orderDetailOpen} 
        onClose={handleCloseOrderDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {`${getOrderDisplayTitle(selectedOrder || undefined)} Details`}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Customer Name
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.customerName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={selectedOrder.status} 
                    color={getStatusColor(selectedOrder.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Amount
                  </Typography>
                  <Typography variant="h6" color="primary">
                    ₹{selectedOrder.totalAmount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedOrder.orderDate).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Delivery Address
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.deliveryAddress}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Delivery Date
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.deliveryDate}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Special Notes
                  </Typography>
                  <Typography variant="body1">
                    {selectedOrder.specialNotes || 'None'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Order Items
              </Typography>
              {selectedOrder.orderItems?.map((item, index) => {
                // Parse selected dishes for plates
                const parseSelectedDishes = (selectedDishesString?: string): Array<{
                  dishId: string;
                  dishName: string;
                  dishPrice: number;
                  quantity: number;
                }> => {
                  if (!selectedDishesString) return [];
                  try {
                    const parsed = JSON.parse(selectedDishesString);
                    return Array.isArray(parsed) ? parsed : [];
                  } catch (e) {
                    console.error('Error parsing selected dishes:', e);
                    return [];
                  }
                };
                
                const selectedDishes = parseSelectedDishes(item.selectedDishes);
                const isPlateWithDishes = item.itemType?.toUpperCase() === 'PLATE' && selectedDishes.length > 0;
                
                return (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6">
                            {item.itemName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.itemType} • {item.businessName}
                          </Typography>
                          {item.bookingDate && (
                            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                              Booking Date: {new Date(item.bookingDate).toLocaleDateString()}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Quantity: {item.quantity} × ₹{item.itemPrice}
                          </Typography>
                          {/* Display selected dishes for plates */}
                          {isPlateWithDishes && (
                            <Box sx={{ mt: 1.5, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                                Selected Dishes:
                              </Typography>
                              {selectedDishes.map((dish, dishIndex) => (
                                <Typography key={dishIndex} variant="caption" color="text.secondary" display="block">
                                  • {dish.dishName} (Qty: {dish.quantity}) - ₹{dish.dishPrice * dish.quantity}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                        <Typography variant="h6" color="primary">
                          ₹{item.itemPrice * item.quantity}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Order Progress
              </Typography>
              <Stepper 
                orientation="horizontal"
                sx={{ 
                  mb: 3,
                  '& .MuiStepLabel-root': {
                    flexDirection: 'column',
                    alignItems: 'center',
                    '& .MuiStepLabel-label': {
                      marginTop: 1,
                      textAlign: 'center'
                    }
                  }
                }}
              >
                {getOrderSteps(selectedOrder).map((step, index) => (
                  <Step 
                    key={index} 
                    completed={step.completed}
                    active={!step.completed && index === getOrderSteps(selectedOrder).findIndex(s => !s.completed)}
                  >
                    <StepLabel 
                      StepIconComponent={() => (
                        <Avatar sx={{ 
                          bgcolor: step.completed ? 'success.main' : 'grey.300',
                          width: 48,
                          height: 48,
                          border: step.completed ? '3px solid #4caf50' : '3px solid #e0e0e0',
                          boxShadow: step.completed ? '0 2px 8px rgba(76, 175, 80, 0.3)' : 'none'
                        }}>
                          {step.icon}
                        </Avatar>
                      )}
                      sx={{
                        '& .MuiStepLabel-label': {
                          fontSize: '0.875rem',
                          fontWeight: step.completed ? 'bold' : 'normal',
                          color: step.completed ? 'success.main' : 'text.primary',
                          mt: 1
                        }
                      }}
                    >
                      {step.label}
                    </StepLabel>
                    <Box sx={{ mt: 1, textAlign: 'center', px: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        {step.description}
                      </Typography>
                      {step.completed && (
                        <Chip 
                          label="✓" 
                          size="small" 
                          color="success" 
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrderDetail}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ClientDashboard;
